import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ControlByIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetControlByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';
/**
 * Maps TRPC control data to match the GraphQL query structure
 */
function mapTrpcControlsToGraphQL(
  trpcData: ControlByIdResponseRow[] | undefined
): GetControlByIdQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    control: trpcData,
  };
}

export const useGetControlById = (
  controlId: string | undefined,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.control.controlById.queryOptions({
      controlId: controlId!,
    }),

    enabled: trpcEnabled && !skip && !!controlId, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcControlsToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
